// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { DivWrapper } from "@guestsupply/XDL/constants";
import systemColors from "@guestsupply/XDL/PreStyle/colors";
import { color, getAllColorsAsDropdownOptions, layoutContainer, xScreen } from "@guestsupply/XDL/Utilities";
import { getFocalPointStyles } from "@insite/client-framework/Common/BasicSelectors";
import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    isFullWidth = "isFullWidth",
    fullWidthStylingType = "fullWidthStylingType",
    fullWidthColor = "fullWidthColor",
    fullWidthImage = "fullWidthImage",
    fullWidthImageFocalPoint = "fullWidthImageFocalPoint",
    customCSS = "customCSS",
    // XNG-Changes
    border = "border",
    borderValue = "borderValue",
    borderColor = "borderColor",
    padding = "padding",
    paddingSize = "paddingSize",
    bgColor = "bgColor",
    customBgColor = "customBgColor",
    alphaColor = "alphaColor",
    containerFullWidth = "containerFullWidth",
    contentFullWidth = "contentFullWidth",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.isFullWidth]: boolean;
        [fields.fullWidthStylingType]: string;
        [fields.fullWidthColor]: string;
        [fields.fullWidthImage]: string;
        [fields.fullWidthImageFocalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.border]: boolean;
        [fields.borderValue]: number;
        [fields.borderColor]: string;
        [fields.padding]: boolean;
        [fields.paddingSize]: string;
        [fields.bgColor]: string;
        [fields.customBgColor]: string;
        [fields.alphaColor]: string;
        [fields.containerFullWidth]: boolean;
        [fields.contentFullWidth]: boolean;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [6, 6, 3, 3, 3],
    4: [6, 6, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const Row = (props: Props) => {
    const {
        fields: {
            columns,
            gap,
            alignment,
            reflow,
            // isFullWidth,
            // fullWidthStylingType,
            // fullWidthColor,
            // fullWidthImage,
            // fullWidthImageFocalPoint,
            customCSS,
            border,
            borderValue,
            borderColor,
            padding,
            paddingSize,
            bgColor,
            customBgColor,
            alphaColor,
            containerFullWidth,
            contentFullWidth,
        },
        id,
    } = props;
    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem key={x} width={reflow ? convertToResponsive(columns[x]) : columns[x]} align={align}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    const backgroundColor = bgColor === "customColor" ? customBgColor : bgColor;
    // const fullWidthBackgroundCss = !isFullWidth
    //     ? undefined
    //     : fullWidthStylingType === "solidColor"
    //     ? css`
    //           background-color: ${fullWidthColor};
    //       `
    //     : fullWidthStylingType === "image"
    //     ? css`
    //           background-image: url("${fullWidthImage}");
    //           background-size: cover;
    //           ${getFocalPointStyles(fullWidthImageFocalPoint)}
    //       `
    //     : undefined;
    const gridContainerCss = css`
        &&&& {
            ${!contentFullWidth ? layoutContainer : ""}
            ${border && `border: ${borderValue};`}
            ${border && `border-color: ${borderColor};`}
            ${padding && `padding: ${paddingSize};`}
        }
    `;
    const finalCss = css`
        ${gridContainerCss}
        ${customCSS}
    `;
    return (
        <>
            <StyledWrapper
                css={css`
                    ${containerFullWidth
                        ? css`
                              width: 100%;
                          `
                        : css`
                              ${layoutContainer}
                          `}
                    background-color: ${color(backgroundColor, parseFloat(alphaColor))};
                `}
            >
                <GridContainer css={finalCss} gap={gap || 0} isFullWidth={contentFullWidth}>
                    {columnElements}
                </GridContainer>
            </StyledWrapper>
        </>
    );
};

const defaultCustomCss = `& {
}`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};
const customTab = {
    displayName: "Customization",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 2,
            },
            // {
            //     name: fields.isFullWidth,
            //     displayName: "Full Width",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     fieldType: "General",
            //     isVisible: (_item, _advancedFeaturesEnabled, theme) => !!theme?.content?.isFullWidth,
            //     sortOrder: 3,
            // },
            // {
            //     name: fields.fullWidthStylingType,
            //     displayName: "Styling",
            //     editorTemplate: "RadioButtonsField",
            //     options: [
            //         {
            //             displayName: "Solid Color",
            //             value: "solidColor",
            //         },
            //         {
            //             displayName: "Image",
            //             value: "image",
            //         },
            //     ],
            //     defaultValue: "solidColor",
            //     horizontal: true,
            //     fieldType: "General",
            //     isVisible: ({ fields: { isFullWidth } }, _advancedFeaturesEnabled, theme) =>
            //         !!theme?.content?.isFullWidth && !!isFullWidth,
            //     sortOrder: 4,
            // },
            // {
            //     name: fields.fullWidthColor,
            //     displayName: "Color",
            //     editorTemplate: "ColorPickerField",
            //     defaultValue: "transparent",
            //     fieldType: "General",
            //     isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
            //         !!theme?.content?.isFullWidth && !!isFullWidth && fullWidthStylingType === "solidColor",
            //     sortOrder: 5,
            // },
            // {
            //     name: fields.fullWidthImage,
            //     displayName: "Image",
            //     editorTemplate: "ImagePickerField",
            //     defaultValue: "",
            //     fieldType: "General",
            //     isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
            //         !!theme?.content?.isFullWidth && !!isFullWidth && fullWidthStylingType === "image",
            //     sortOrder: 6,
            // },
            // {
            //     name: fields.fullWidthImageFocalPoint,
            //     displayName: "Focal Point",
            //     editorTemplate: "DropDownField",
            //     options: [
            //         { displayName: "Top Left", value: "topLeft" },
            //         { displayName: "Top Center", value: "topCenter" },
            //         { displayName: "Top Right", value: "topRight" },
            //         { displayName: "Center Left", value: "centerLeft" },
            //         { displayName: "Center", value: "center" },
            //         { displayName: "Center Right", value: "centerRight" },
            //         { displayName: "Bottom Left", value: "bottomLeft" },
            //         { displayName: "Bottom Center", value: "bottomCenter" },
            //         { displayName: "Bottom Right", value: "bottomRight" },
            //     ],
            //     defaultValue: "center",
            //     fieldType: "General",
            //     hideEmptyOption: true,
            //     isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
            //         !!theme?.content?.isFullWidth && !!isFullWidth && fullWidthStylingType === "image",
            //     sortOrder: 7,
            // },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            {
                name: "customCSS",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }
                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                name: fields.bgColor,
                displayName: "Background Color",
                editorTemplate: "DropDownField",
                options: [
                    ...getAllColorsAsDropdownOptions(systemColors),
                    {
                        displayName: "Custom color",
                        value: "customColor",
                    },
                ],
                defaultValue: "",
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.customBgColor,
                displayName: "Custom color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                tab: customTab,
                isVisible: (item: HasFields) => item.fields.bgColor === "customColor",
            },
            {
                name: fields.alphaColor,
                displayName: "Color - Opacity",
                editorTemplate: "TextField",
                defaultValue: "1",
                fieldType: "General",
                placeholder: "add color opacity",
                tab: customTab,
                isVisible: (item: HasFields) => item.fields.bgColor,
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return `A value must be provided.`;
                    }
                    return isNumeric(value) && parseFloat(value) <= 1 && parseFloat(value) > 0 ? null : `Invalid Input`;
                },
            },
            {
                name: fields.border,
                displayName: "Border",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.borderValue,
                displayName: "Border",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
                placeholder: "Enter: border style",
                tab: customTab,
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return `A value must be provided.`;
                    }
                    return CSS.supports("border", value) ? null : `Invalid Input`;
                },
                isVisible: item => item.fields.border,
            },
            {
                name: fields.borderColor,
                editorTemplate: "ColorPickerField",
                defaultValue: systemColors.common.border,
                fieldType: "General",
                tab: customTab,
                isVisible: item => item.fields.border,
            },
            {
                name: fields.padding,
                displayName: "Padding",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.paddingSize,
                displayName: "Padding Size",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
                placeholder: "Enter: 5px or any padding style",
                tab: customTab,
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return `A value must be provided.`;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
                isVisible: item => item.fields.padding,
            },

            {
                name: fields.containerFullWidth,
                displayName: "Container Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.contentFullWidth,
                displayName: "Content Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customTab,
                isVisible: item => item.fields.containerFullWidth,
            },
        ],
    },
};

export default widgetModule;
